import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Coffee, MapPin, Building2, ShieldCheck, TrendingUp, HeartHandshake } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal } from "./Reveal";
import { KineticWords } from "./KineticWords";

const Counter = ({ to, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / 1500, 1);
      setVal(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);

  return <span ref={ref}>{val}{suffix}</span>;
};

const cardIcons = [ShieldCheck, TrendingUp, Coffee];

const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

/** Entrance: fade-only on mobile (velké y/rotace překrývaly obsah pod kartami). */
const enterCard = (i) =>
  isMobile
    ? { initial: { opacity: 0 }, whileInView: { opacity: 1 }, viewport: { once: true, margin: "-30px" }, transition: { duration: 0.4, delay: i * 0.1 } }
    : { initial: { opacity: 0, y: 90, rotate: i === 0 ? -2.5 : 2.5 }, whileInView: { opacity: 1, y: 0, rotate: 0 }, viewport: { once: true, margin: "-80px" }, transition: { type: "spring", stiffness: 90, damping: 16, delay: i * 0.15 } };

export const About = () => {
  const { t, lang } = useLanguage();

  const partners = [
    {
      name: t.about.partner1Name,
      title: t.about.partner1Title[lang],
      bio: t.about.partner1Bio[lang],
      initials: "RZ",
      testid: "partner-card-raffaele",
    },
    {
      name: t.about.partner2Name,
      title: t.about.partner2Title[lang],
      bio: t.about.partner2Bio[lang],
      initials: "OŠ",
      testid: "partner-card-ondrej",
    },
  ];

  return (
    <section id="about" data-testid="about-section" className="relative overflow-hidden py-28 sm:py-36 lg:py-44">
      <div className="pointer-events-none absolute -left-40 bottom-24 h-[420px] w-[420px] rounded-full blur-[140px]" style={{ background: "radial-gradient(circle, rgba(245,158,11,0.06) 0%, transparent 65%)" }} aria-hidden="true" />
      <div className="mesh-orb orb-a absolute -left-40 top-10 h-[480px] w-[480px] rounded-full" style={{ background: "rgba(13,148,136,0.14)" }} aria-hidden="true" />
      <div className="mesh-orb orb-b absolute -right-32 bottom-0 h-[420px] w-[420px] rounded-full" style={{ background: "rgba(30,58,95,0.45)" }} aria-hidden="true" />
      <span className="hero-particle" style={{ left: "8%", top: "18%", width: 3, height: 3, animationDelay: "0.6s" }} aria-hidden="true" />
      <span className="hero-particle" style={{ left: "90%", top: "30%", width: 2, height: 2, animationDelay: "1.9s" }} aria-hidden="true" />

      <div className="relative mx-auto max-w-[1500px] px-4 sm:px-8 lg:px-12">
        <span className="font-mono text-[11px] font-bold uppercase tracking-[0.35em] text-teal-400">
          01 · {t.about.badge[lang]}
        </span>
        <h2
          data-testid="about-heading"
          className="font-display mt-6 max-w-5xl text-4xl font-extrabold uppercase leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl"
        >
          <KineticWords key={lang} text={t.about.heading[lang]} />
        </h2>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">{t.about.bio[lang]}</p>

        <div className="mt-20 grid gap-6 lg:grid-cols-2">
          {partners.map((p, i) => (
            <motion.div
              key={p.testid}
              initial={{ opacity: 0, y: 90, rotate: i === 0 ? -2.5 : 2.5 }}
              whileInView={{ opacity: 1, y: 0, rotate: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ type: "spring", stiffness: 90, damping: 16, delay: i * 0.15 }}
              className={i === 1 ? "lg:translate-y-16" : ""}
            >
              <motion.div
                data-testid={p.testid}
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                className="glass-dark relative h-full overflow-hidden rounded-3xl p-8 sm:p-12"
              >
                <span
                  aria-hidden="true"
                  className="text-outline-teal font-display pointer-events-none absolute -right-4 -top-10 select-none text-[10rem] font-black leading-none opacity-30 sm:text-[12rem]"
                >
                  {p.initials}
                </span>
                <div className="relative">
                  <span className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-teal-400/70">
                    Partner 0{i + 1}
                  </span>
                  <h3 className="font-display mt-4 text-3xl font-bold text-white sm:text-4xl">{p.name}</h3>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-teal-300">{p.title}</p>
                  <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">{p.bio}</p>
                  <div className="mt-7 flex items-center gap-2.5">
                    {cardIcons.map((Icon, j) => (
                      <motion.span
                        key={j}
                        initial={{ opacity: 0, scale: 0 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4 + j * 0.12, type: "spring", stiffness: 300, damping: 18 }}
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-teal-400/10 text-teal-300"
                      >
                        <Icon size={15} />
                      </motion.span>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div
            data-testid="about-stats-panel"
            className="glass-dark mt-24 grid grid-cols-1 divide-y divide-teal-400/15 rounded-3xl sm:grid-cols-3 sm:divide-x sm:divide-y-0"
          >
            {t.about.stats.map((s) => (
              <div key={s.testid} data-testid={s.testid} className="group px-6 py-9 text-center sm:py-12">
                <p className="font-display flex items-center justify-center gap-2 text-3xl font-extrabold text-white sm:text-4xl">
                  {s.icon === "coffee" && (
                    <Coffee size={22} className="text-teal-300 transition-transform duration-300 group-hover:-rotate-12 group-hover:scale-125" />
                  )}
                  {s.icon === "pin" && (
                    <MapPin size={22} className="text-teal-300 transition-transform duration-300 group-hover:-translate-y-1 group-hover:scale-125" />
                  )}
                  {s.type === "counter" ? <Counter to={s.value} suffix={s.suffix} /> : s.value[lang]}
                </p>
                <p className="mt-2 text-[11px] font-medium uppercase tracking-wider text-slate-400 sm:text-xs">
                  {s.label[lang]}
                </p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            data-testid="about-office-card"
            className="glass-dark mt-6 flex flex-col items-center gap-3 rounded-3xl px-6 py-7 text-center sm:flex-row sm:justify-between sm:text-left"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-teal-400/30 bg-[#0A192F] text-teal-300">
                <Building2 size={20} />
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-teal-300">{t.about.officeBadge[lang]}</p>
                <p className="text-sm font-semibold text-white" data-testid="about-office-address">{t.about.officeAddress[lang]}</p>
              </div>
            </div>
            <div className="font-mono text-xs leading-relaxed text-slate-400" data-testid="about-ico">
              <p>{t.about.officeIco1[lang]}</p>
              <p className="mt-0.5">{t.about.officeIco2[lang]}</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2} className="mt-10">
          <span className="inline-flex items-center gap-2 rounded-full border border-teal-400/30 bg-teal-400/10 px-5 py-2 text-xs font-semibold text-teal-200">
            <HeartHandshake size={15} />
            {lang === "cs" ? "Oba dva · pojištění, finance i investice" : "Both of us · insurance, finance & investments"}
          </span>
        </Reveal>
      </div>
    </section>
  );
};
